import { apiRequest, type ApiResponse } from '../services/api';
import type {
  OrganizationWorkspace,
  OrganizationVersion,
  OrganizationVersionDetail,
  OrganizationVersionPage,
  OrganizationalGenerationAnswers,
  StructurePreview,
  StructureApplyRequest,
  StructureApplyResult,
} from './model';

export class OrganizationApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public currentRevision?: number,
    public code?: string
  ) {
    super(message);
    this.name = 'OrganizationApiError';
  }
}

export function unwrapOrganizationResponse<T>(response: ApiResponse<T>): T {
  if (!response.success || response.data === undefined) {
    throw new OrganizationApiError(
      response.error || 'The organization service is unavailable. Your changes are retained.',
      response.status || 0,
      response.currentRevision,
      response.code
    );
  }
  return response.data;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  return unwrapOrganizationResponse(await apiRequest<T>(path, options));
}

export const organizationApi = {
  workspace: (companyId: string) =>
    request<OrganizationWorkspace>(`/organization/${companyId}/workspace`),
  versions: (companyId: string, cursor?: string) =>
    request<OrganizationVersionPage>(
      `/organization/${companyId}/versions${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`
    ),
  version: (companyId: string, versionId: string) =>
    request<OrganizationVersionDetail>(`/organization/${companyId}/versions/${versionId}`),
  labelVersion: (
    companyId: string,
    versionId: string,
    label: string | null,
    note?: string | null
  ) =>
    request<OrganizationVersion>(`/organization/${companyId}/versions/${versionId}`, {
      method: 'PATCH',
      body: JSON.stringify({ label, ...(note !== undefined ? { note } : {}) }),
    }),
  preview: (
    companyId: string,
    expectedRevision: number,
    answers: OrganizationalGenerationAnswers,
    signal?: AbortSignal
  ) =>
    request<StructurePreview>(`/organization/${companyId}/preview`, {
      method: 'POST',
      body: JSON.stringify({ expectedRevision, answers }),
      signal,
    }),
  apply: (companyId: string, payload: StructureApplyRequest) =>
    request<StructureApplyResult>(`/organization/${companyId}/apply`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  mutate: async <T = unknown>(
    path: string,
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    data: Record<string, unknown>,
    expectedRevision: number
  ) => {
    const response = await apiRequest<T>(path, {
      method,
      body: JSON.stringify({ ...data, expectedRevision }),
    });
    if (!response.success) unwrapOrganizationResponse(response);
    return response.data ?? response;
  },
};
